import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { resolveImg, imgFallback } from "@/lib/api";
import { LotCard } from "@/components/LotCard";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, CheckCircle, ArrowRight, CurrencyInr, Buildings, GlobeHemisphereEast, Lock,
  UploadSimple, MagnifyingGlass, Handshake, Bank, ClipboardText, Storefront, Truck, Package,
  ImageSquare, Play, Table as TableIcon, Scales, Archive, Gear, MapPin,
} from "@phosphor-icons/react";

const HERO_BG = "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CARD_IMG = "https://images.pexels.com/photos/10834810/pexels-photo-10834810.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

/* ------------ Sample hero lot visual ------------ */
function SampleLotVisual() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-white p-6 shadow-[0_24px_60px_-24px_rgba(17,24,39,0.28)]">
      <img src={resolveImg(CARD_IMG)} onError={imgFallback} alt="Bulk genuine spare parts" className="absolute right-0 top-0 h-48 w-[42%] object-cover" />
      <span className="stat absolute right-5 top-36 z-10 rounded-lg bg-brand px-3 py-1.5 text-center text-sm font-bold leading-tight text-white shadow-lg">
        56%<br /><span className="text-[10px] font-semibold uppercase tracking-wide">Below MRP</span>
      </span>

      <div className="relative pr-[44%]">
        <span className="text-xs font-bold tracking-wide text-brand">LOT SH-2026-018</span>
        <h3 className="mt-2 text-2xl font-bold text-ink">Toyota Genuine Parts</h3>
        <p className="text-sm text-subtext">Bulk Genuine Spare Parts</p>
        <div className="mt-6 flex gap-10">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-subtext">MRP Value</div>
            <div className="stat text-lg font-bold text-ink">₹28.40 <span className="text-sm font-semibold text-subtext">Lakh</span></div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-subtext">Available At</div>
            <div className="stat text-lg font-bold text-brand">₹12.50 <span className="text-sm font-semibold">Lakh</span></div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border pt-5">
        {[
          { ic: Package, v: "1,428", l: "SKUs" },
          { ic: Gear, v: "3,860", l: "Parts" },
          { ic: MapPin, v: "Maharashtra", l: "Location" },
          { ic: Package, v: "New & Unused", l: "Stock Type" },
        ].map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy-50"><s.ic size={16} className="text-darkteal" /></span>
            <div className="min-w-0">
              <div className="stat truncate text-sm font-bold text-ink">{s.v}</div>
              <div className="truncate text-[10px] uppercase tracking-wider text-subtext">{s.l}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-5 border-t border-border pt-4 text-xs text-subtext">
        <span className="flex items-center gap-1.5"><ImageSquare size={15} className="text-teal" /> 6 Photos</span>
        <span className="flex items-center gap-1.5"><Play size={15} weight="fill" className="text-teal" /> Video Available</span>
        <span className="flex items-center gap-1.5"><TableIcon size={15} className="text-teal" /> Excel Inventory</span>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, sub, right }) {
  return (
    <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
      <div className="max-w-2xl">
        {eyebrow && <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>}
        <h2 className="mt-2 text-3xl font-bold tracking-tight text-ink md:text-4xl">{title}</h2>
        {sub && <p className="mt-3 text-base leading-relaxed text-subtext md:text-lg">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

export default function Home() {
  const [lots, setLots] = useState([]);

  useEffect(() => {
    api.get("/lots", { params: { page_size: 6, sort: "value" } }).then(({ data }) => setLots(data.lots)).catch(() => {});
  }, []);

  return (
    <div className="pb-16 md:pb-0">
      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden bg-palered">
        <div className="absolute inset-0" aria-hidden>
          <img src={resolveImg(HERO_BG)} onError={imgFallback} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-palered via-palered/90 to-palered/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-palered/80 via-transparent to-palered/20" />
        </div>
        <div className="absolute inset-0 dot-grid opacity-30" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 lg:grid-cols-2 lg:px-8 lg:py-24">
          <div>
            <span className="inline-block rounded-full border border-brand/20 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand">
              B2B Spare Parts Liquidation Marketplace
            </span>
            <h1 className="mt-6 text-4xl font-bold leading-[1.08] tracking-tight text-ink md:text-5xl lg:text-6xl">
              Turn Dead Spare Parts Inventory <span className="text-brand">Into Cash.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-subtext">
              Sell bulk dead stock, slow-moving inventory, excess stock and dealership closure stock to verified B2B spare-parts buyers across India and overseas.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="h-12 rounded-lg bg-brand px-7 text-base font-semibold text-white hover:bg-brand-hover" data-testid="hero-list-stock">
                <Link to="/register?role=seller">List Your Stock <ArrowRight className="ml-1" /></Link>
              </Button>
              <Button asChild variant="outline" className="h-12 rounded-lg border-border bg-white px-7 text-base font-semibold text-ink hover:bg-navy-50" data-testid="hero-browse-lots">
                <Link to="/lots">Browse Available Lots</Link>
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm">
              <span className="flex items-center gap-2 text-subtext"><CheckCircle weight="fill" className="text-ok" size={18} /> ₹5 Lakh+ Bulk Lots</span>
              <span className="flex items-center gap-2 text-subtext"><CheckCircle weight="fill" className="text-ok" size={18} /> No Listing Charges</span>
              <span className="flex items-center gap-2 rounded-lg border border-teal/40 bg-teal/10 px-3 py-1.5 font-semibold text-darkteal"><ShieldCheck weight="fill" className="text-darkteal" size={18} /> Protected by SparesHub Escrow</span>
            </div>
          </div>

          <div>
            <SampleLotVisual />
          </div>
        </div>
      </section>

      {/* ---------- TRUST STRIP ---------- */}
      <section className="border-y border-border bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px overflow-hidden md:grid-cols-4">
          {[
            { icon: CurrencyInr, k: "₹5 Lakh+", v: "Minimum Transaction Value" },
            { icon: Buildings, k: "B2B Only", v: "Professional Buyers & Sellers" },
            { icon: ShieldCheck, k: "Escrow Protected", v: "Payments Managed by SparesHub", teal: true },
            { icon: GlobeHemisphereEast, k: "India + Export", v: "Domestic & International Buyers" },
          ].map((c) => (
            <div key={c.k} className="flex items-start gap-3 bg-white p-6">
              <c.icon weight="regular" className={`mt-0.5 h-6 w-6 shrink-0 ${c.teal ? "text-darkteal" : "text-brand"}`} />
              <div>
                <div className="text-lg font-bold text-ink">{c.k}</div>
                <div className="text-sm text-subtext">{c.v}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- FEATURED LOTS ---------- */}
      <section className="bg-navy-50">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
          <SectionHead
            eyebrow="Live Marketplace"
            title="Explore Bulk Spare Parts Lots"
            sub="Browse dead stock, excess inventory and dealership liquidation lots available for bulk purchase."
            right={<Button asChild variant="ghost" className="hidden text-ink hover:text-brand md:inline-flex" data-testid="home-view-all"><Link to="/lots">View All Lots →</Link></Button>}
          />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {lots.map((lot, i) => <LotCard key={lot.id} lot={lot} index={i} />)}
          </div>
          {lots.length === 0 && <div className="rounded-xl border border-dashed border-border bg-white p-12 text-center text-subtext">Lots are being prepared — check back shortly.</div>}
          <div className="mt-8 md:hidden">
            <Button asChild variant="outline" className="w-full rounded-lg border-border"><Link to="/lots">View All Lots</Link></Button>
          </div>
        </div>
      </section>

      {/* ---------- BUYER SECTION ---------- */}
      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 lg:grid-cols-2 lg:px-8 lg:py-20">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">For Buyers</span>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-ink md:text-4xl">Buy Bulk Spare Parts at Liquidation Prices</h2>
            <p className="mt-4 text-lg leading-relaxed text-subtext">Discover dealership, distributor and retailer inventory available for bulk purchase. Review stock details, download inventory sheets and submit an indicative offer directly through the platform.</p>
            <ul className="mt-6 space-y-3">
              {[
                "Genuine B2B liquidation opportunities",
                "Detailed Excel inventory available",
                "Photos and stock walkthrough videos",
                "Seller identity kept confidential during negotiation",
                "Transactions protected through SparesHub Escrow",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-ink"><CheckCircle weight="fill" className="mt-0.5 h-5 w-5 shrink-0 text-ok" /> {b}</li>
              ))}
            </ul>
            <Button asChild className="mt-8 h-12 rounded-lg bg-brand px-7 font-semibold text-white hover:bg-brand-hover" data-testid="buyer-browse"><Link to="/lots">Browse Available Lots</Link></Button>
          </div>
          <div className="rounded-2xl border border-border bg-navy-50 p-6">
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: ClipboardText, t: "Inventory Sheets", d: "Full SKU-level Excel per lot" },
                { icon: ImageSquare, t: "Verified Media", d: "Photos + stock videos" },
                { icon: Lock, t: "Confidential", d: "Seller hidden pre-deal" },
                { icon: ShieldCheck, t: "Escrow", d: "Payment protection", teal: true },
              ].map((c) => (
                <div key={c.t} className="rounded-xl border border-border bg-white p-5">
                  <c.icon className={`h-7 w-7 ${c.teal ? "text-darkteal" : "text-brand"}`} />
                  <div className="mt-3 font-semibold text-ink">{c.t}</div>
                  <div className="text-sm text-subtext">{c.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- SELLER SECTION ---------- */}
      <section className="bg-palered">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">For Sellers</span>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-ink md:text-4xl">Have Dead or Slow-Moving Inventory?</h2>
            <p className="mt-4 text-lg leading-relaxed text-subtext">Turn idle spare-parts inventory into working capital. Upload your bulk stock, let SparesHub showcase it to B2B buyers, and pay only when a transaction successfully closes.</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { t: "Free Listing", d: "No upfront fee to list your inventory." },
              { t: "Confidential", d: "Your business identity remains hidden from buyers before the deal." },
              { t: "B2B Buyers", d: "Reach retailers, distributors, exporters and bulk buyers." },
              { t: "SparesHub Managed", d: "We handle buyer enquiries, negotiation support, escrow and transaction coordination." },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-border bg-white p-6">
                <div className="text-base font-bold text-ink">{c.t}</div>
                <p className="mt-2 text-sm leading-relaxed text-subtext">{c.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button asChild className="h-12 rounded-lg bg-brand px-7 font-semibold text-white hover:bg-brand-hover" data-testid="seller-list"><Link to="/register?role=seller">List Your Stock</Link></Button>
            <span className="text-sm text-subtext">Minimum expected transaction value: <strong className="text-ink">₹5,00,000</strong></span>
          </div>
        </div>
      </section>

      {/* ---------- HOW IT WORKS ---------- */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">Process</span>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-ink md:text-4xl">Simple. Managed. Secure.</h2>
            <p className="mt-3 text-lg text-subtext">SparesHub manages the transaction so buyers and sellers can focus on the deal.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-5">
            {[
              { icon: UploadSimple, t: "Seller Uploads Stock", d: "Seller uploads the inventory Excel, photographs and stock video." },
              { icon: MagnifyingGlass, t: "SparesHub Reviews", d: "We review the inventory and prepare the lot for listing." },
              { icon: Handshake, t: "Buyers Express Interest", d: "Interested B2B buyers review the inventory and submit indicative offers." },
              { icon: ClipboardText, t: "SparesHub Coordinates", d: "Our team manages negotiations, verification and inspection requirements." },
              { icon: Bank, t: "Deal Through Escrow", d: "Buyer payment is handled through SparesHub Escrow and the transaction is completed." },
            ].map((s, i) => (
              <div key={s.t} className="relative rounded-xl border border-border bg-white p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-palered"><s.icon className="h-6 w-6 text-brand" /></div>
                <div className="stat mt-4 text-xs font-bold text-subtext">STEP {i + 1}</div>
                <h3 className="mt-1 font-semibold text-ink">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-subtext">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ESCROW SECTION ---------- */}
      <section className="bg-darkteal-50">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
          <div className="rounded-3xl border border-teal/30 bg-white p-8 md:p-12">
            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-teal/15 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-darkteal">
                  <ShieldCheck weight="fill" size={14} /> Transaction Protection
                </span>
                <h2 className="mt-4 text-3xl font-bold tracking-tight text-ink md:text-4xl">Protected by SparesHub Escrow</h2>
                <p className="mt-4 text-lg leading-relaxed text-subtext">Large inventory transactions require trust. SparesHub stands between the buyer and seller, manages the payment process and helps resolve transaction disputes.</p>
                <p className="mt-6 text-sm font-semibold text-darkteal">Secure Payment · Managed Settlement · Transaction Support</p>
              </div>
              <div className="grid gap-4">
                {[
                  { icon: Lock, t: "Buyer Protection", d: "Buyer pays through SparesHub instead of directly dealing with an unknown seller." },
                  { icon: Bank, t: "Seller Protection", d: "Seller receives payment through a structured SparesHub-managed transaction." },
                  { icon: Scales, t: "Dispute Mediation", d: "SparesHub coordinates verification and mediates disputes where required." },
                ].map((c) => (
                  <div key={c.t} className="flex items-start gap-4 rounded-xl border border-border bg-navy-50 p-5">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-teal/20"><c.icon className="h-6 w-6 text-darkteal" /></div>
                    <div>
                      <div className="font-semibold text-ink">{c.t}</div>
                      <p className="text-sm text-subtext">{c.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- WHO CAN SELL ---------- */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-ink md:text-4xl">Who Can List Inventory?</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Storefront, t: "Automobile Dealerships", d: "Closure stock, excess inventory and ageing inventory." },
              { icon: Truck, t: "Spare Parts Distributors", d: "Dead and slow-moving stock." },
              { icon: Package, t: "Spare Parts Retailers", d: "Bulk surplus and ageing stock." },
              { icon: GlobeHemisphereEast, t: "Importers", d: "Excess or discontinued inventory." },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-border bg-white p-6 transition-colors hover:border-brand/30">
                <c.icon className="h-8 w-8 text-darkteal" />
                <div className="mt-4 font-semibold text-ink">{c.t}</div>
                <p className="mt-1 text-sm text-subtext">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- WHAT CAN BE LISTED ---------- */}
      <section className="bg-navy-50">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-ink md:text-4xl">Inventory Suitable for Liquidation</h2>
          <div className="mt-8 flex flex-wrap gap-3">
            {["Dead Stock", "Slow-Moving Inventory", "Excess Inventory", "Dealership Closure Stock", "Distributor Clearance", "Special Bulk Lots", "New Old Stock"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-ink">
                <Archive size={16} className="text-brand" /> {t}
              </span>
            ))}
          </div>
          <p className="mt-6 text-sm text-subtext">Minimum expected transaction value: <strong className="text-ink">₹5 lakh</strong></p>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="bg-brand">
        <div className="mx-auto max-w-7xl px-5 py-16 text-center lg:px-8 lg:py-20">
          <h2 className="mx-auto max-w-3xl text-3xl font-bold tracking-tight text-white md:text-4xl">Your Dead Inventory Could Be Someone Else's Opportunity.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-white/85">List bulk automotive spare-parts inventory with SparesHub and reach serious B2B buyers.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button asChild className="h-12 rounded-lg bg-white px-8 text-base font-semibold text-brand hover:bg-white/90" data-testid="final-list-stock">
              <Link to="/register?role=seller">List Your Stock</Link>
            </Button>
            <Link to="/lots" className="text-base font-semibold text-white underline-offset-4 hover:underline" data-testid="final-browse">Browse Lots →</Link>
          </div>
        </div>
      </section>

      {/* ---------- MOBILE STICKY CTA ---------- */}
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-2 border-t border-border bg-white p-3 md:hidden">
        <Button asChild variant="outline" className="rounded-lg border-darkteal/40 text-darkteal"><Link to="/lots" data-testid="sticky-browse">Browse Lots</Link></Button>
        <Button asChild className="rounded-lg bg-brand text-white hover:bg-brand-hover"><Link to="/register?role=seller" data-testid="sticky-list">List Stock</Link></Button>
      </div>
    </div>
  );
}
